import { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface Criteria {
  label: string;
  met: boolean;
}

export const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  // Critères simplifiés : 8 caractères, 1 lettre, 1 chiffre
  const criteria: Criteria[] = useMemo(() => [
    { label: '8 caractères minimum', met: password.length >= 8 },
    { label: 'Au moins une lettre', met: /[a-zA-Z]/.test(password) },
    { label: 'Au moins un chiffre', met: /[0-9]/.test(password) },
  ], [password]);

  // Critères bonus (non obligatoires)
  const bonusCriteria: Criteria[] = useMemo(() => [
    { label: 'Majuscule (bonus)', met: /[A-Z]/.test(password) },
    { label: 'Caractère spécial (bonus)', met: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password) },
  ], [password]);

  const strength = useMemo(() => {
    const requiredMet = criteria.filter(c => c.met).length;
    const bonusMet = bonusCriteria.filter(c => c.met).length;
    
    if (requiredMet === 0) return { level: 0, label: '', color: '' };
    if (requiredMet < 3) return { level: 1, label: 'Incomplet', color: 'bg-destructive' };
    if (bonusMet === 0) return { level: 2, label: 'Correct', color: 'bg-amber-500' };
    if (bonusMet === 1) return { level: 3, label: 'Fort', color: 'bg-emerald-500' };
    return { level: 4, label: 'Très fort', color: 'bg-emerald-600' };
  }, [criteria, bonusCriteria]);

  if (!password) return null;

  return (
    <div className="mt-2 space-y-3">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Force du mot de passe</span>
          {strength.label && (
            <span className={cn(
              "font-medium",
              strength.level <= 1 && "text-destructive",
              strength.level === 2 && "text-amber-600",
              strength.level >= 3 && "text-emerald-600"
            )}>
              {strength.label}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-all duration-300",
                level <= strength.level ? strength.color : "bg-muted"
              )}
            />
          ))}
        </div>
      </div>

      {/* Critères obligatoires */}
      <div className="space-y-1">
        {criteria.map((criterion, index) => (
          <div
            key={index}
            className={cn(
              "flex items-center gap-1.5 text-xs transition-colors duration-200",
              criterion.met ? "text-emerald-600" : "text-muted-foreground"
            )}
          >
            {criterion.met ? (
              <Check className="h-3 w-3 flex-shrink-0" />
            ) : (
              <X className="h-3 w-3 flex-shrink-0" />
            )}
            <span>{criterion.label}</span>
          </div>
        ))}
      </div>

      {/* Critères bonus (affichés seulement si au moins un est rempli ou mot de passe assez long) */}
      {password.length >= 8 && (
        <div className="pt-1 border-t border-border/50 space-y-1">
          <span className="text-xs text-muted-foreground">Bonus sécurité :</span>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {bonusCriteria.map((criterion, index) => (
              <div
                key={index}
                className={cn(
                  "flex items-center gap-1.5 text-xs transition-colors duration-200",
                  criterion.met ? "text-emerald-600" : "text-muted-foreground/60"
                )}
              >
                {criterion.met ? (
                  <Check className="h-3 w-3 flex-shrink-0" />
                ) : (
                  <span className="h-3 w-3 flex-shrink-0 text-center">○</span>
                )}
                <span>{criterion.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
